import { useEffect, useRef } from "react";

/**
 * Subscribe to the admin SSE stream and invoke `onEvent({type, ...})` for each.
 *   - Connection is opened only when `enabled` (defaults to true) AND a token is present.
 *   - Auto-reconnects with backoff if the browser closes the stream.
 *   - Cleans up on unmount.
 */
export default function useAdminEvents(onEvent, { enabled = true } = {}) {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;
    const token = sessionStorage.getItem("admin_token");
    if (!token) return;

    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${base}/api/admin/events?token=${encodeURIComponent(token)}`;

    let es = null;
    let closed = false;
    let retryMs = 1000;
    let retryTimer = null;

    const handleEvent = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        handlerRef.current?.(data);
      } catch (err) {
        console.warn("[sse] bad event payload:", err?.message);
      }
    };

    const connect = () => {
      if (closed) return;
      try {
        es = new EventSource(url);
        es.addEventListener("hello", () => { retryMs = 1000; });
        // Known event types we care about
        ["so_started", "so_finished", "so_photo_added", "so_created", "so_updated", "so_deleted", "so_assigned", "invoice_updated", "message"].forEach((type) => {
          es.addEventListener(type, handleEvent);
        });
        es.onerror = () => {
          if (closed) return;
          try { es.close(); } catch (_) { /* ignore */ }
          retryTimer = setTimeout(connect, retryMs);
          retryMs = Math.min(retryMs * 2, 30000);
        };
      } catch (err) {
        console.warn("[sse] connect failed:", err?.message);
        retryTimer = setTimeout(connect, retryMs);
      }
    };
    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (es) try { es.close(); } catch (_) { /* ignore */ }
    };
  }, [enabled]);
}
